import React, { useEffect, useRef } from 'react';
import { Image } from "@project-stories/elements/Image/Image";
import { Button } from '@src/stories/elements/Button/Button';
import { useHubSpotForm } from '@src/hooks/useHubSpotForm';

function SolutionBlockCTA({ block, globals }) {
  const { openForm } = useHubSpotForm({
    portalId: globals?.hubspot?.portal_id,
    formId: block.settings.section_hubspot_form_id || globals?.hubspot?.form_id,
    region: block.settings.section_hubspot_region || globals?.hubspot?.form_region,
  });

  const isContactForm = block.settings.use_form || false;

  return (
    <Button
      styleType="button"
      style="outline-secondary"
      icon="right-arrow-alt"
      label={`${block.settings.cta_text}`}
      link={isContactForm ? undefined : (block.settings.cta_url || "")}
      className="solution-block__cta mt-9"
      onClick={isContactForm ? () => openForm() : undefined}
    />
  );
}

export default function Grid({ settings, blocks }) {
  const layoutType = 'grid';
  const rightRefs = useRef([]);
  const leftRefs = useRef([]);
  const leftContainerRef = useRef(null);

  useEffect(() => {
    const showImage = (index) => {
      leftRefs.current.forEach((el, i) => {
        if (!el) return;
        el.classList.toggle('opacity-0', i !== index);
        el.classList.toggle('opacity-100', i === index);
        el.classList.toggle('z-[1]', i === index);
      });
    };

    // Initially show the first image
    showImage(0);

    const handleScroll = () => {
      // Find the topmost item that is still (even partially) in the viewport
      for (let i = 0; i < rightRefs.current.length; i++) {
        const el = rightRefs.current[i];
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        if (rect.top < window.innerHeight && rect.bottom > 0) {
          showImage(i);
          return;
        }
      }
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => window.removeEventListener('scroll', handleScroll);
  }, [blocks]);

  // Ensure the last right block is tall enough so the sticky left container
  // never gets clipped when the last block has less content.
  // Uses ResizeObserver so it re-runs whenever the left container changes size
  // (e.g. after images finish loading).
  useEffect(() => {
    const applyMinHeight = () => {
      if (!leftContainerRef.current) return;
      const lastRight = rightRefs.current[rightRefs.current.length - 1];
      if (!lastRight) return;
      lastRight.style.minHeight = '';
      const leftHeight = leftContainerRef.current.offsetHeight;
      if (lastRight.offsetHeight < leftHeight) {
        lastRight.style.minHeight = `${leftHeight}px`;
      }
    };

    applyMinHeight();
    window.addEventListener('resize', applyMinHeight);

    const observer = new ResizeObserver(applyMinHeight);
    if (leftContainerRef.current) {
      observer.observe(leftContainerRef.current);
    }

    return () => {
      window.removeEventListener('resize', applyMinHeight);
      observer.disconnect();
    };
  }, [blocks]);

  return (
    <div className={`solution-${layoutType} flex flex-col gap-16`}>
      <div className="flex flex-col items-center gap-11 text-center">
        <div className="flex flex-col max-w-[630px]">
          {settings.section.tagline && (
            <div className={`${settings.section.tagline_style}`}>
              {settings.section.tagline}
            </div>
          )}
          {settings.section.title && (
            <div className={`${settings.section.title_font_type}`}>
              {settings.section.title}
            </div>
          )}
        </div>
        {settings.section.subtitle && (
          <div className={`max-w-[436px] ${settings.section.subtitle_style}`}>
            {settings.section.subtitle}
          </div>
        )}
      </div>

      {blocks.length > 0 && (
        <div className="solution-blocks grid grid-cols-2 gap-20 items-start">
          <div ref={leftContainerRef} className="solution-blocks--left sticky top-5">
            {blocks.map((block, index) => (
              <div
                key={index}
                ref={(el) => (leftRefs.current[index] = el)}
                className={`solution-block transition-opacity duration-500 ${index === 0 ? 'opacity-100' : 'opacity-0'}`}
                // Ensure all images occupy the same space to prevent layout shift
                style={{ position: index === 0 ? 'relative' : 'absolute', top: 0, left: 0, width: '100%' }}
              >
                {block.settings.image && (
                  <Image
                    imageUrl={block.settings.image}
                    alt={block.settings.title}
                    classes={[
                      block.settings.width ? `w-[${block.settings.width}%]` : 'w-auto',
                      block.settings.max_width ? `max-w-[${block.settings.max_width}px]` : '',
                      block.settings.width || block.settings.max_width || block.settings.max_height ? 'mx-auto' : '',
                      'h-auto',
                      block.settings.max_height ? `max-h-[${block.settings.max_height}px]` : '',
                      (block.settings.width || block.settings.max_width) && block.settings.max_height ? 'object-cover' : '',
                      `rounded-[${block.settings.image_border_radius}px]`,
                      'bg-white/0 shadow-[0_20px_25px_-5px_rgba(0,0,0,0.10),0_8px_10px_-6px_rgba(0,0,0,0.10)]',
                    ].filter(Boolean).join(' ')}
                  />
                )}

                {block.settings.cta_text && (
                  <SolutionBlockCTA block={block} globals={settings.globals} />
                )}
              </div>
            ))}
          </div>

          <div className="solution-blocks--right flex flex-col gap-20">
            {blocks.map((block, index) => (
              <div
                key={index}
                ref={(el) => (rightRefs.current[index] = el)}
                className="solution-block"
              >
                {block.settings.title && (
                  <div className={`${block.settings.title_font_type}`}>
                    {block.settings.title}
                  </div>
                )}
                {block.settings.subtitle && (
                  <div className={`mt-1 ${block.settings.subtitle_font_style}`}>
                    {block.settings.subtitle}
                  </div>
                )}
                {(() => {
                  const blockServices = (settings.blocks_services || []).find((b: any) => b.id === block.id);
                  const serviceList = (blockServices?.services_list || [])
                    .map((s: string) => s.trim())
                    .filter(Boolean);
                  return serviceList.length > 0 ? (
                    <ul className="solution-block__services mt-12 flex flex-col gap-6">
                      {serviceList.map((service: string, i: number) => (
                        <li key={i} className="flex items-start gap-3">
                          {block.settings.service_icon && (
                            <Image
                              imageUrl={block.settings.service_icon}
                              useSrcSet={false}
                              classes="w-6 h-6 flex-shrink-0"
                            />
                          )}
                          <span className={`${block.settings.service_font_style}`}>
                            {service}
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : null;
                })()}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
};